import React, { useState, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    EmbeddedCheckoutProvider,
    EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { X, Loader2 } from 'lucide-react';
import api from '../lib/api';

// Initialize Stripe with publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export const EmbeddedCheckoutModal = ({ 
    isOpen, 
    onClose, 
    checkoutType, // 'subscription' or 'credits'
    planId,       // For subscriptions
    packageId,    // For credits
    billingPeriod = 'monthly', // 'monthly' or 'annual'
    onSuccess 
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchClientSecret = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await api.post('/payments/embedded/create-session', {
                checkout_type: checkoutType,
                plan_id: planId,
                package_id: packageId,
                billing_period: billingPeriod
            });
            
            return response.data.client_secret;
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to initialize checkout');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [checkoutType, planId, packageId, billingPeriod]);

    const options = {
        fetchClientSecret
    };

    const handleComplete = useCallback(() => {
        if (onSuccess) {
            onSuccess();
        }
        onClose();
    }, [onSuccess, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />
            
            {/* Modal */}
            <div className="relative bg-background rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <h2 className="text-lg font-semibold">
                        {checkoutType === 'subscription' ? 'Complete Your Subscription' : 'Purchase Credits'}
                    </h2>
                    <Button variant="ghost" size="sm" onClick={onClose}>
                        <X className="h-5 w-5" />
                    </Button>
                </div>
                
                {/* Content */}
                <div className="p-4 overflow-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
                    {error ? (
                        <div className="text-center py-8">
                            <p className="text-destructive mb-4">{error}</p>
                            <Button onClick={() => setError(null)}>Try Again</Button>
                        </div>
                    ) : loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                            <span className="ml-3 text-muted-foreground">Loading checkout...</span>
                        </div>
                    ) : (
                        <div id="checkout" className="min-h-[400px]">
                            <EmbeddedCheckoutProvider
                                stripe={stripePromise}
                                options={options}
                            >
                                <EmbeddedCheckout onComplete={handleComplete} />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmbeddedCheckoutModal;
